import { useMyInvokablePlaybooksQuery } from "@qw/graphql";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  cn,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
} from "@qw/ui";
import { type FC, type PropsWithChildren, useCallback, useEffect, useMemo, useRef } from "react";

import type { SlashCommandState } from "../../hooks/use-slash-commands";
import { useChatForm } from "./use-chat-form";

export interface PlaybookOption {
  id: string;
  name: string;
}

export interface PlaybooksSlashCommandMenuProps {
  commandState: SlashCommandState;
  onClose: () => void;
}

export const PlaybooksSlashCommandMenu: FC<PropsWithChildren<PlaybooksSlashCommandMenuProps>> = ({
  commandState,
  onClose,
  children,
}) => {
  const { input, setInput, textInputRef } = useChatForm();

  const commandRef = useRef<HTMLDivElement>(null);

  // myInvokablePlaybooks returns owned + subscribed `Workflow` and
  // `WorkflowStep` playbooks. `Knowledge` is embedded in the system prompt
  // and never invokable. `WorkflowStep` playbooks are surfaced here so a
  // broker can manually invoke a step playbook as a recovery escape hatch
  // when a parent orchestrator's LLM skips or inlines a step; foreground
  // LLM discovery still hides them so the LLM can't auto-invoke them
  // without a user mentioning the UUID via this slash menu.
  const { data: myInvokablePlaybooksData } = useMyInvokablePlaybooksQuery();
  const playbooks = useMemo(
    (): PlaybookOption[] =>
      myInvokablePlaybooksData?.myInvokablePlaybooks?.map((p) => ({ id: p.id, name: p.name })) ?? [],
    [myInvokablePlaybooksData]
  );

  // Filter playbooks based on query
  const filteredPlaybooks = useMemo(() => {
    if (!commandState.query) {
      return playbooks;
    }

    const query = commandState.query.toLowerCase();
    return playbooks.filter((playbook) => playbook.name.toLowerCase().includes(query));
  }, [playbooks, commandState.query]);

  // Handle playbook insertion from slash command
  const handleSelectPlaybook = useCallback(
    (playbook: PlaybookOption) => {
      // Build new input: everything before "/" + playbook reference + everything after the query
      const textBeforeSlash = input.slice(0, commandState.cursorPosition);
      const textAfterQuery = input.slice(commandState.cursorPosition + 1 + commandState.query.length);
      const playbookReference = `Run playbook "${playbook.name}" (ID: ${playbook.id})`;
      const newInput = `${textBeforeSlash}${playbookReference} ${textAfterQuery}`;

      setInput(newInput);

      // Ensure textarea keeps focus with multiple attempts
      const refocusTextarea = () => {
        const newCursorPos = textBeforeSlash.length + playbookReference.length + 1;
        textInputRef?.current?.setSelectionRange(newCursorPos, newCursorPos);
        textInputRef?.current?.focus();
      };

      // Try immediately
      requestAnimationFrame(() => {
        refocusTextarea();
        // And again after a short delay to ensure popover is fully closed
        setTimeout(refocusTextarea, 100);
      });

      onClose();
    },
    [input, setInput, commandState, onClose, textInputRef]
  );

  // Handle keyboard navigation
  useEffect(() => {
    if (!commandState.isOpen) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle real user events, not synthetic events we dispatch
      // (synthetic events bubble back up and would cause infinite recursion)
      if (!e.isTrusted) {
        return;
      }

      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }

      // Forward arrow keys and Enter to the Command component
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || (e.key === "Enter" && !e.shiftKey)) {
        e.preventDefault();

        // Dispatch the event to the Command component
        if (commandRef.current) {
          const keyboardEvent = new KeyboardEvent("keydown", {
            key: e.key,
            code: e.code,
            bubbles: true,
            cancelable: true,
          });
          commandRef.current.dispatchEvent(keyboardEvent);
        }
      }
    };

    globalThis.addEventListener("keydown", handleKeyDown);
    return () => globalThis.removeEventListener("keydown", handleKeyDown);
  }, [commandState.isOpen, onClose]);

  return (
    <Popover open={commandState.isOpen} onOpenChange={(open) => !open && onClose()}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        sideOffset={8}
        collisionPadding={8}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          // Keep the textarea focused
          textInputRef?.current?.focus();
        }}
        onCloseAutoFocus={(e) => {
          e.preventDefault();
          // Focus the textarea after close
          textInputRef?.current?.focus();
        }}
        style={{ minWidth: "calc(var(--radix-popover-trigger-width) + 2px)" }}
        className="overflow-hidden p-0"
      >
        <Command ref={commandRef} shouldFilter={false}>
          <CommandList style={{ maxHeight: "min(28rem, var(--radix-popover-content-available-height))" }}>
            <CommandEmpty>No playbooks found.</CommandEmpty>

            {filteredPlaybooks.length > 0 && (
              <>
                <h3 className="text-xs font-medium py-2 px-3 text-muted-foreground">Run a playbook</h3>
                <Separator />
              </>
            )}

            <CommandGroup className={cn({ "p-0": filteredPlaybooks.length === 0 })}>
              {filteredPlaybooks.map((playbook) => (
                <CommandItem key={playbook.id} value={playbook.id} onSelect={() => handleSelectPlaybook(playbook)}>
                  {playbook.name}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
